import React, { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserAvatar } from "@clerk/clerk-react";
import { Globe, LogOut, MessageSquare, UserRound, Users, Inbox } from "lucide-react";
import { getInitials } from "@/utils/getInitials";

export default function AvatarMenu({ user, onSignOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const menuId = useId();

  const displayName = user?.fullName || user?.email || "User";
  const email = user?.email || "";
  const role = String(user?.publicMetadata?.role || "").trim().toLowerCase();
  const isAdmin = role === "admin";
  let initials = getInitials(displayName);
  if (!initials || initials.length < 2) {
    initials = getInitials(email || "User");
  }
  initials = String(initials || "US").slice(0, 2).toUpperCase();
  const imageUrl = user?.imageUrl || "";

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  const projectKeyFromPath = (() => {
    const match = String(location.pathname || "").match(/^\/app\/projects\/([^/]+)/);
    return match?.[1] || "";
  })();
  const scopedPath = (section, fallback) =>
    projectKeyFromPath
      ? `/app/projects/${projectKeyFromPath}/${section}${location.search || ""}`
      : fallback;

  const handleFeedback = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent("actara:open-feedback"));
  };

  const handleSignOut = async () => {
    setOpen(false);
    if (onSignOut) {
      await onSignOut();
    }
    navigate("/");
  };

  const menuItemClass =
    "flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50";

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center rounded-full bg-transparent p-0 text-sm text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={displayName}
      >
        {imageUrl ? (
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full">
            <UserAvatar
              appearance={{
                elements: {
                  userAvatarBox: "h-9 w-9 rounded-full",
                  userAvatarImage: "h-9 w-9 rounded-full object-cover",
                },
              }}
            />
          </span>
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[linear-gradient(135deg,#A5B4FF_0%,#7C6CFF_55%,#4F46E5_100%)] text-sm font-semibold tracking-[-0.02em] text-white hover:opacity-90">
            {initials.slice(0, 2)}
          </span>
        )}
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          tabIndex={-1}
          className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <div className="px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">{displayName}</div>
            {user?.email ? (
              <div className="text-xs text-slate-500">{user.email}</div>
            ) : null}
            {isAdmin ? (
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Admin
              </div>
            ) : null}
          </div>

          <div className="border-t border-slate-100 py-2">
            <button
              type="button"
              role="menuitem"
              onClick={() => handleNavigate(scopedPath("account", "/account"))}
              className={menuItemClass}
            >
              <UserRound className="h-4 w-4 text-slate-500" />
              My Account
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => handleNavigate(scopedPath("team", "/team"))}
              className={menuItemClass}
            >
              <Users className="h-4 w-4 text-slate-500" />
              Team
            </button>
            {isAdmin ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleNavigate("/admin/feedback")}
                  className={menuItemClass}
                >
                  <Inbox className="h-4 w-4 text-slate-500" />
                  Feedback Inbox
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleNavigate("/admin/demo-archive")}
                  className={menuItemClass}
                >
                  <Inbox className="h-4 w-4 text-slate-500" />
                  Demo Archive
                </button>
              </>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={handleFeedback}
              className={menuItemClass}
            >
              <MessageSquare className="h-4 w-4 text-slate-500" />
              Give Feedback
            </button>
          </div>

          <div className="border-t border-slate-100 py-2">
            <button
              type="button"
              role="menuitem"
              onClick={() => handleNavigate("/")}
              className={menuItemClass}
            >
              <Globe className="h-4 w-4 text-slate-500" />
              Visit Website
            </button>
          </div>

          <div className="border-t border-slate-100 py-2">
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              className={menuItemClass}
            >
              <LogOut className="h-4 w-4 text-slate-500" />
              Log out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
